import classNames from 'classnames';
import React from 'react';
import { Modal } from 'react-bootstrap';
import { ModalProps } from 'react-bootstrap/Modal';
import { useIntl } from 'react-intl';

import useCloseConfirmation from '@app/hooks/useCloseConfirmation';

import Loader from '../Loader';

import ConfirmModal from './ConfirmModal';

interface ModalDefaultProps {
    body?: React.ReactNode;
    bodyClassName?: string;
    centered?: boolean;
    className?: string;
    dialogClassName?: string;
    backdropClassName?: string;
    closeButton?: boolean;
    footer?: React.ReactNode | ((handleClose: () => void) => React.ReactNode);
    hideModal?: () => void;
    icon?: React.ReactNode;
    isDirty?: boolean;
    loading?: boolean;
    onEnter?: () => void;
    show?: boolean;
    size?: ModalProps['size'];
    subtitle?: React.ReactNode;
    title?: React.ReactNode;
    titleVariant?: string;
    backdrop?: ModalProps['backdrop'];
}

const ModalDefault = ({
    body = null,
    bodyClassName = '',
    centered = false,
    className = '',
    dialogClassName,
    backdropClassName,
    closeButton = true,
    footer = null,
    hideModal = () => {},
    icon = null,
    isDirty = false,
    loading = false,
    onEnter = () => {},
    show = false,
    size,
    subtitle = null,
    title = null,
    titleVariant = 'text-primary',
    backdrop,
}: ModalDefaultProps) => {
    const intl = useIntl();
    const { showConfirmClose, closeDirectly, requestClose, handleConfirmClose, handleCancelClose } =
        useCloseConfirmation();

    const handleClose = () => (isDirty ? requestClose(() => hideModal?.()) : closeDirectly(() => hideModal?.()));

    const resolvedFooter = typeof footer === 'function' ? footer(handleClose) : footer;

    return (
        <>
            <Modal
                size={size}
                show={show}
                enforceFocus={false}
                onEnter={onEnter}
                onHide={handleClose}
                centered={centered}
                className={className}
                dialogClassName={dialogClassName}
                backdropClassName={backdropClassName}
                backdrop={backdrop}
            >
                {showConfirmClose && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#000',
                            opacity: 0.2,
                            zIndex: 10,
                        }}
                    />
                )}
                {loading && <Loader style={{ width: '5rem', height: '5rem' }} overlay />}
                <Modal.Header className="border-bottom-0 text-center" closeButton={closeButton} />
                {(icon || title) && (
                    <Modal.Title className="text-center">
                        <div className={classNames('px-lg-0 px-5 text-center', titleVariant)}>
                            {icon ?? null}
                            <div>{title}</div>
                        </div>
                        {subtitle && <div className="font-size-base">{subtitle}</div>}
                    </Modal.Title>
                )}
                <Modal.Body className={bodyClassName}>{body ?? null}</Modal.Body>
                {resolvedFooter && <Modal.Footer>{resolvedFooter}</Modal.Footer>}
            </Modal>

            {isDirty && (
                <ConfirmModal
                    showModal={showConfirmClose}
                    setShowModal={(v) => !v && handleCancelClose()}
                    title={intl.formatMessage({ id: 'MODAL.CLOSE_CONFIRM.TITLE' })}
                    description={intl.formatMessage({ id: 'MODAL.CLOSE_CONFIRM.DESCRIPTION' })}
                    confirmLabel={intl.formatMessage({ id: 'MODAL.CLOSE_CONFIRM.CTA.MAIN' })}
                    cancelLabel={intl.formatMessage({ id: 'MODAL.CLOSE_CONFIRM.CTA.SECOND' })}
                    onConfirm={handleConfirmClose}
                    onCancel={handleCancelClose}
                />
            )}
        </>
    );
};

export default ModalDefault;
